// The checklist itself: percent, progress bar, one row per step, footer CTA.
//
// Extracted so it can live in two shells:
//   - OnboardingChecklistSheet — the dismissable global modal (welcome nudge,
//     header ring, avatar dropdown, Settings card).
//   - Screens/Books/CsocProfileGate — a blocking overlay inside School of
//     Chess, which must NOT be a screen-covering modal because the left menu
//     has to stay reachable.
//
// Pass `onDismiss` for the dismissable shell; omit it for the blocking one and
// the "Skip for now" link disappears.

import React from 'react';
import {View, TouchableOpacity} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {useOnboardingProgress} from '@/Store/onboardingProgressStore';
import {StepKey} from '@/Utils/onboardingSteps';

const BROWN = CCColors.Primary.Brown;
const CREAM = CCColors.Primary.lightBrown;
const GREY = CCColors.TextColor.GreyText;

// Short helper line per step, shown under the label.
const STEP_SUBS: Record<string, string> = {
  profile: 'Your name & where you play from',
  username: 'How other players see you',
  connect_accounts: 'Pull your games & rating in',
};

interface Props {
  /** Called with a step's route when the user chooses to go and do it. */
  onNavigate: (route: string) => void;
  /** Omit to make the checklist non-dismissable (no "Skip for now"). */
  onDismiss?: () => void;
  /** Replaces the skip link when there isn't one — explains why it's stuck. */
  footerNote?: string;
  /**
   * Show (and count) only these steps. Used by the School of Chess gate, which
   * requires the profile and username but not a linked Lichess/Chess.com
   * account. Omit for the full checklist.
   */
  stepKeys?: StepKey[];
}

export default function ChecklistBody({
  onNavigate,
  onDismiss,
  footerNote,
  stepKeys,
}: Props) {
  const progress = useOnboardingProgress();

  // Percent has to be recomputed over the subset, not taken from the store —
  // otherwise a gate on 2 steps would read the 3-step number and could sit at
  // 67% while showing "2 of 2 done".
  const steps = stepKeys
    ? progress.steps.filter(s => stepKeys.includes(s.key))
    : progress.steps;
  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const percent = total === 0 ? 100 : Math.round((completed / total) * 100);

  const allDone = percent >= 100;
  const nextStep = steps.find(s => !s.done && s.route);

  return (
    <View style={{paddingHorizontal: 4, paddingBottom: 8, minWidth: 288}}>
      <View style={{flexDirection: 'row', alignItems: 'baseline', marginTop: 2}}>
        <CCText fontSize={30} fontWeight="semibold" style={{color: BROWN}}>
          {percent}%
        </CCText>
        <CCText fontSize={13} style={{color: GREY, marginLeft: 8}}>
          {allDone ? 'profile complete' : `${completed} of ${total} done`}
        </CCText>
      </View>
      <View
        style={{
          height: 8,
          borderRadius: 100,
          backgroundColor: CREAM,
          overflow: 'hidden',
          marginTop: 8,
          marginBottom: 16,
        }}>
        <View
          style={{
            width: `${Math.max(4, percent)}%`,
            height: '100%',
            borderRadius: 100,
            backgroundColor: allDone ? CCColors.Green : CCColors.Primary.Default,
          }}
        />
      </View>

      {steps.map(s => (
        <TouchableOpacity
          key={s.key}
          disabled={s.done || !s.route}
          activeOpacity={0.7}
          onPress={() => onNavigate(s.route)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'stretch',
            backgroundColor: s.done ? CCColors.White : CREAM,
            borderRadius: 12,
            paddingVertical: 12,
            paddingHorizontal: 12,
            marginBottom: 8,
            borderWidth: 1,
            borderColor: s.done ? CCColors.BorderLight : 'transparent',
          }}>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 25,
              backgroundColor: s.done ? CCColors.Green : CCColors.LightYellow,
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
            }}>
            <Icon
              name={s.done ? 'check' : s.icon}
              size={14}
              color={s.done ? CCColors.White : BROWN}
              solid={s.done}
            />
          </View>
          <View style={{flex: 1}}>
            <CCText
              fontSize={14}
              fontWeight="semibold"
              style={{
                color: s.done ? GREY : BROWN,
                textDecorationLine: s.done ? 'line-through' : 'none',
              }}>
              {s.label}
            </CCText>
            {!s.done && STEP_SUBS[s.key] ? (
              <CCText fontSize={11.5} style={{color: GREY, marginTop: 1}}>
                {STEP_SUBS[s.key]}
              </CCText>
            ) : null}
          </View>
          {!s.done && s.route ? (
            <View
              style={{
                width: 26,
                height: 26,
                borderRadius: 100,
                backgroundColor: CCColors.Primary.Default,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Icon name="chevron-right" size={11} color={CCColors.White} />
            </View>
          ) : null}
        </TouchableOpacity>
      ))}

      <View style={{marginTop: 8}}>
        {allDone || !nextStep ? (
          <CCButton onPress={() => onDismiss?.()}>
            <CCText fontSize={14} fontWeight="semibold">
              Done
            </CCText>
          </CCButton>
        ) : (
          <>
            <CCButton animated onPress={() => onNavigate(nextStep.route)}>
              <CCText fontSize={14} fontWeight="semibold">
                Continue — {nextStep.label.toLowerCase()}
              </CCText>
            </CCButton>
            {onDismiss ? (
              <TouchableOpacity
                onPress={onDismiss}
                style={{
                  marginTop: 10,
                  alignSelf: 'center',
                  paddingVertical: 4,
                }}>
                <CCText fontSize={13} style={{color: GREY}}>
                  Skip for now
                </CCText>
              </TouchableOpacity>
            ) : footerNote ? (
              <CCText
                fontSize={12}
                style={{color: GREY, marginTop: 10, textAlign: 'center'}}>
                {footerNote}
              </CCText>
            ) : null}
          </>
        )}
      </View>
    </View>
  );
}
